import { useState } from 'react';
import { Modal, Button, RadioButtonGroup, RadioButton } from '@carbon/react';
import { Add } from '@carbon/react/icons';
import { StandingErrorToast } from 'components/error-toast';
import { errorBodyMessage } from 'utils/api-errors';
import styles from './add-document-modal.module.scss';
import { useConfigurationOptionsAtom, documentTypeAtom } from './atoms';
import {
  getPromptsForDocumentType,
  getDefaultPrompt,
  extensionsFromMimeTypes,
  DEFAULT_ACCEPTED_FORMATS,
} from './utils';
import { useDocumentUpload } from './use-document-upload';
import { ConfigureProvisionFields } from './provision-fields';
import { DocumentUploadField } from './document-upload-field';
import { CompletionConfigurationResponse } from 'types/api-responses';
import { useAtomValue, useAtom } from 'jotai';

const formatExtensionsForLabel = (extensions: string[]): string => {
  const labels = extensions.map((ext) => ext.replace('.', '').toUpperCase());
  if (labels.length <= 1) return labels.join('');
  if (labels.length === 2) return labels.join(' or ');
  return `${labels.slice(0, -1).join(', ')}, or ${labels[labels.length - 1]}`;
};

const AddDocumentModalContainer = () => {
  const configurationOptionsAtom =
    useConfigurationOptionsAtom('/configurations');
  const configuration = useAtomValue(configurationOptionsAtom);
  const configurationObject = configuration.data;
  if (!configurationObject || 'error' in configurationObject) return null;

  return <AddDocumentModal configurationObject={configurationObject} />;
};

const AddDocumentModal = ({
  configurationObject,
}: {
  configurationObject: CompletionConfigurationResponse;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { documentTypes } = configurationObject;
  const [storedDocumentType, setStoredDocumentType] = useAtom(documentTypeAtom);
  const documentType = documentTypes.some(
    (dt) => dt.value === storedDocumentType
  )
    ? storedDocumentType
    : (documentTypes[0]?.value ?? '');
  const getPromptCategory = (type: string) =>
    documentTypes.find((dt) => dt.value === type)?.promptCategory ?? '';
  const filteredPrompts = getPromptsForDocumentType(
    configurationObject.promptConfigurations,
    getPromptCategory(documentType)
  );

  const {
    upload,
    setUpload,
    ocrModel,
    setOcrModel,
    llmModel,
    setLlmModel,
    promptVersion,
    setPromptVersion,
    isSubmitting,
    isUploading,
    isUploadComplete,
    submitDisabled,
    handleFileUpload,
    handleSubmit,
    reset,
  } = useDocumentUpload({
    configurationObject,
    documentType,
    filteredPrompts,
    onSuccess: (data) => {
      window.location.href = data.href;
    },
    onError: (error) => {
      setSubmitError(errorBodyMessage(error, 'Problem uploading document'));
    },
  });

  const setDocumentType = (type: string) => {
    setStoredDocumentType(type);
    const prompts = getPromptsForDocumentType(
      configurationObject.promptConfigurations,
      getPromptCategory(type)
    );
    setPromptVersion(getDefaultPrompt(prompts));
  };

  const selectedDocumentTypeOption = documentTypes.find(
    (dt) => dt.value === documentType
  );
  const acceptedExtensions = extensionsFromMimeTypes(
    selectedDocumentTypeOption?.acceptedFormats ?? DEFAULT_ACCEPTED_FORMATS
  );
  const uploadHelperText = `Upload a single ${formatExtensionsForLabel(
    acceptedExtensions
  )} file (max 500 MB limit)`;
  // Preserve acronyms (all-caps labels like "AFE") verbatim so the helper text
  // reads "For AFE documents" rather than "For afe documents".
  const promptHelperText =
    selectedDocumentTypeOption?.promptVersionLabel ??
    (() => {
      const label = selectedDocumentTypeOption?.label ?? 'selected';
      const formatted =
        label === label.toUpperCase() ? label : label.toLowerCase();
      return `For ${formatted} documents`;
    })();

  const resetModal = () => {
    setIsOpen(false);
    reset();
    setSubmitError(null);
  };

  const closeModal = () => {
    if (upload.status !== 'uploading' && !isSubmitting) {
      resetModal();
    }
  };

  return (
    <>
      {/* Stays up until closed: it renders outside the modal's focus trap. */}
      {submitError && (
        <StandingErrorToast
          message={submitError}
          onClose={() => setSubmitError(null)}
        />
      )}
      <Button
        size="sm"
        renderIcon={Add}
        onClick={() => {
          const courthouseValue =
            documentTypes.find((dt) => dt.value === 'courthouse')?.value ??
            documentTypes[0]?.value ??
            '';
          setDocumentType(courthouseValue);
          setIsOpen(true);
        }}
      >
        <span>Add Document</span>
      </Button>
      <Modal
        open={isOpen}
        onRequestClose={closeModal}
        modalHeading="Add Document"
        primaryButtonText={isSubmitting ? 'Adding Document...' : 'Add Document'}
        secondaryButtonText="Cancel"
        primaryButtonDisabled={submitDisabled}
        onRequestSubmit={() => {
          setSubmitError(null);
          handleSubmit();
        }}
        className={styles.addDocumentModal}
        shouldSubmitOnEnter={false}
      >
        <div className={styles.sectionWrapper}>
          <RadioButtonGroup
            legendText="Document Type"
            name="document-type"
            valueSelected={documentType}
            onChange={(value: string | number | undefined) =>
              setDocumentType(value as string)
            }
            className={styles.radioGroup}
            disabled={isSubmitting}
          >
            {documentTypes.map((opt) => (
              <RadioButton
                key={opt.value}
                id={`document-type-${opt.value}`}
                value={opt.value}
                labelText={opt.label}
              />
            ))}
          </RadioButtonGroup>
        </div>

        <div className={styles.sectionWrapper}>
          <DocumentUploadField
            heading="Upload files"
            helperText={uploadHelperText}
            labelText="Drag and drop files here or click to upload"
            accept={acceptedExtensions}
            upload={upload}
            onAddFiles={handleFileUpload}
            onDelete={() => !isSubmitting && setUpload(undefined)}
            disabled={isUploading || isSubmitting || isUploadComplete}
          />
        </div>

        <div className={styles.sectionWrapper}>
          <ConfigureProvisionFields
            extractionConfigurations={
              configurationObject.extractionConfigurations
            }
            completionConfigurations={
              configurationObject.completionConfigurations
            }
            filteredPrompts={filteredPrompts}
            ocrModel={ocrModel}
            onOcrChange={setOcrModel}
            llmModel={llmModel}
            onLlmChange={setLlmModel}
            promptVersion={promptVersion}
            onPromptChange={setPromptVersion}
            promptHelperText={promptHelperText}
            disabled={isSubmitting}
          />
        </div>
      </Modal>
    </>
  );
};

export default AddDocumentModalContainer;
